import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Typography, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import FilePreview from 'components/Common/FilePreview/FilePreview';
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useEsignAnalyses from 'hooks/useEsignAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { esignAnalysesSelectors } from 'features/esignAnalyses';

const EsignAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { esign_analysis_id } = useParams();
  const { getEsignAnalysis, clearLocalActiveEsignAnalysis } = useEsignAnalyses();
  const { getEsignAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const esignAnalysesFetching = useCustomSelector(esignAnalysesSelectors.esignAnalysesIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchEsignAnalysis = useCallback((id) => {
    setOpen(true);
    getEsignAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { esign_analysis } } = payload;
        handleSetData(esign_analysis);
      }
    })
  }, [getEsignAnalysis]);

  const handleSetData = (esign_analysis) => {
    setInput(esign_analysis?.input);
    setResult(esign_analysis?.result);
  };

  const handleExiting = () => {
    setInput('');
    setResult(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl10e`);
    clearLocalActiveEsignAnalysis();
    clearLocalAnalysesEvents();
  };
  
  const handleClose = () => setOpen(false);

  const fetchEsignAnalysesEvents = useCallback((offset, esign_analysis_id) => {
    getEsignAnalysesEvents({ offset, esign_analysis_id });
  }, [ getEsignAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchEsignAnalysesEvents(0, esign_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!esign_analysis_id) fetchEsignAnalysis(esign_analysis_id);
  }, [esign_analysis_id, fetchEsignAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        {esignAnalysesFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {esign_analysis_id}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'  }}>
            <TextField
              fullWidth
              margin='normal'
              label='Input'
              multiline
              minRows={5}
              maxRows={8}
              disabled
              value={input ?? ''}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#073842",
                }
              }}
            />
            {result?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Result:</Typography>
              <FilePreview
                key={result?.id}
                type={result?.content_type}
                name={result?.filename}
                url={result?.url}
              />
            </Box>}
          </Box>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={esign_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchEsignAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <EsignAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);